import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export const detailLineCopyText = (value: React.ReactNode) => {
  if (value == null || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    const text = String(value).trim();
    return text === '—' ? '' : text;
  }
  return '';
};

const writeClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
};

export const HouseholdDetailLine: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => {
  const copyText = detailLineCopyText(value);
  const display = value == null || value === '' ? '—' : value;
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    },
    []
  );

  const onCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!copyText) return;
    const ok = await writeClipboard(copyText);
    if (!ok) return;
    setCopied(true);
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, py: 0.4, alignItems: 'flex-start' }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120, flexShrink: 0, pt: 0.2 }}>
        {label}
      </Typography>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'flex-start',
          gap: 0.25,
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <Typography variant="body2" sx={{ wordBreak: 'break-word', pt: 0.15 }}>
          {display}
        </Typography>
        <Tooltip title={copied ? 'Copied' : `Copy ${label}`}>
          <span>
            <IconButton
              size="small"
              aria-label={`Copy ${label}`}
              data-testid={`copy-detail-${label}`}
              disabled={!copyText}
              onClick={onCopy}
              sx={{ mt: -0.25 }}
            >
              {copied ? <CheckIcon fontSize="inherit" /> : <ContentCopyIcon fontSize="inherit" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};
